// TECH DEBT: typing is hard for this need time to fix
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button } from '@/components/ui/button';
import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import { PDKFilterIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useMyDealsFilter from '@/hooks/use-my-deals-filter';
import { HOTKEYS } from '@/lib/hotkeys';
import { capitalize, isEqual, last, values } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import AllFiltersComboBoxPopover from './combobox-popovers/all-filters-combobox-popover';
import AllFilterChip from './filter-chips/all-filter-chip';
import { useFilteredDealsContext } from './filtered-deals/filtered-deals-provider';
import {
  type ComboBoxState,
  CREATED_DATE_FILTER_OPTIONS,
  type CreatedDateFiltersState,
  DELIVERY_DATE_FILTER_OPTIONS,
  type DeliveryDateFilterOptions,
  type DeliveryDateFiltersState,
  type DeliveryPreferenceFiltersState,
  type DeliveryQueuedFiltersState,
  type DeliveryStageFiltersState,
  type FilterState,
  type FinanceManagerFiltersState,
  type FundingMethodFiltersState,
  LAST_UPDATED_DATE_FILTER_OPTIONS,
  type LastUpdatedDateFiltersState,
  type LenderFiltersState,
  type PickupDateFiltersState,
  type PriceLockedFiltersState,
  type PriorityFiltersState,
  type RapidReconStepFiltersState,
  type SalesRepFiltersState,
  type StatusFilters,
  type StatusFiltersState,
  type TradeInFiltersState,
} from './types';

const STATUS_FILTER_INITIAL_STATE: StatusFiltersState = {
  filterMetaState: {
    type: 'status',
    position: 0,
    isNegated: false,
    isCurrentlyBeingBuilt: true,
  },
  selectedFilters: {
    draft: false,
    new: false,
    in_progress: false,
    awaiting_delivery: false,
    delivered: false,
    done: false,
    cancelled: false,
    returning: false,
    returned: false,
  },
};

const DELIVERY_STAGE_FILTER_INITIAL_STATE: DeliveryStageFiltersState = {
  filterMetaState: {
    type: 'delivery_stage',
    position: 0,
    isNegated: false,
    isCurrentlyBeingBuilt: true,
  },
  selectedFilters: {
    none: false,
    pending_pickup: false,
    in_delivery: false,
    delivered: false,
  },
};

const RAPID_RECON_STEP_FILTER_INITIAL_STATE: RapidReconStepFiltersState = {
  filterMetaState: {
    type: 'rapid_recon_step',
    position: 0,
    isNegated: false,
    isCurrentlyBeingBuilt: true,
  },
  selectedFilters: {
    'Purchase/Auction': false,
    'Listing + DB Input': false,
    'In Transit': false,
    'Vehicle Hold': false,
    'Offsite Repairs': false,
    'Intake Inspection': false,
    'One Shot Photo': false,
    'Battery Diagnostics': false,
    'Full Inspection': false,
    'Order Parts': false,
    'Parts Arrived': false,
    'Mechanical Repair': false,
    'Ready For Pickup': false,
    'Body Shop': false,
    Cosmetic: false,
    Wheels: false,
    'Paintless Dent Repair': false,
    Detail: false,
    'Full Photos': false,
    'Ready For Sale': false,
    Wholesale: false,
    'Needs Attention': false,
    None: false,
  },
};
const DELIVERY_PREFERENCE_FILTER_INITIAL_STATE: DeliveryPreferenceFiltersState =
  {
    filterMetaState: {
      type: 'delivery_preference',
      position: 0,
      isNegated: false,
      isCurrentlyBeingBuilt: true,
    },
    selectedFilters: {
      none: false,
      delivery: false,
      local_delivery: false,
      pickup: false,
      extended: false,
    },
  };

const PRIORITY_FILTER_INITIAL_STATE: PriorityFiltersState = {
  filterMetaState: {
    type: 'priority',
    position: 0,
    isNegated: false,
    isCurrentlyBeingBuilt: true,
  },
  selectedFilters: {
    high: false,
    medium: false,
    low: false,
    none: false,
  },
};

const DealFilters = observer(() => {
  const [comboBoxState, setComboBoxState] = useState<ComboBoxState>('filter');

  const {
    filters,
    setFilters,
    matchPreference,
    setMatchPreference,
    shouldApplyFilteredDealsIds,
  } = useFilteredDealsContext();

  const myDealsFilter = useMyDealsFilter();

  const setFilterAtIndex = useCallback(
    (index: number) => {
      return <T extends FilterState>(callBack: (prev: T) => T) => {
        setFilters((prev) => {
          return [
            ...prev.slice(0, index),
            callBack(prev[index] as T),
            ...prev.slice(index + 1),
          ];
        });
      };
    },
    [setFilters],
  );
  const removeFilterAtIndex = useCallback(
    (index: number) => () => {
      setFilters((prev) => {
        return prev.slice(0, index).concat(prev.slice(index + 1));
      });
    },
    [setFilters],
  );

  const setLastFilter = useCallback(setFilterAtIndex(filters.length - 1), [
    setFilterAtIndex,
    filters,
  ]);

  const comboBoxOnChange = useCallback(
    (value: string) => {
      if (comboBoxState === 'filter') {
        if (value === 'status') {
          setComboBoxState('status');
          setFilters((prev) => {
            const newFilter = {
              ...STATUS_FILTER_INITIAL_STATE,
            };

            return [...prev, newFilter];
          });
        } else if (value === 'delivery_stage') {
          setComboBoxState('delivery_stage');
          setFilters((prev) => {
            const newFilter = {
              ...DELIVERY_STAGE_FILTER_INITIAL_STATE,
            };

            return [...prev, newFilter];
          });
        } else if (value === 'rapid_recon_step') {
          setComboBoxState('rapid_recon_step');
          setFilters((prev) => {
            const newFilter = {
              ...RAPID_RECON_STEP_FILTER_INITIAL_STATE,
            };

            return [...prev, newFilter];
          });
        } else if (value === 'delivery_preference') {
          setComboBoxState('delivery_preference');
          setFilters((prev) => {
            const newFilter = {
              ...DELIVERY_PREFERENCE_FILTER_INITIAL_STATE,
            };

            return [...prev, newFilter];
          });
        } else if (value === 'priority') {
          setComboBoxState('priority');
          setFilters((prev) => {
            const newFilter = {
              ...PRIORITY_FILTER_INITIAL_STATE,
            };

            return [...prev, newFilter];
          });
        } else if (value === 'finance_manager') {
          setComboBoxState('finance_manager');
          setFilters((prev) => {
            const newFilter: FinanceManagerFiltersState = {
              filterMetaState: {
                type: 'finance_manager',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: {},
            };

            return [...prev, newFilter];
          });
        } else if (value === 'sales_rep') {
          setComboBoxState('sales_rep');
          setFilters((prev) => {
            const newFilter: SalesRepFiltersState = {
              filterMetaState: {
                type: 'sales_rep',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: {},
            };

            return [...prev, newFilter];
          });
        } else if (value === 'funding_method') {
          setComboBoxState('funding_method');
          setFilters((prev) => {
            const newFilter: FundingMethodFiltersState = {
              filterMetaState: {
                type: 'funding_method',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: {
                cash: false,
                loan: false,
                outside_financing: false,
                none: false,
              },
            };

            return [...prev, newFilter];
          });
        } else if (value === 'lender') {
          setComboBoxState('lender');
          setFilters((prev) => {
            const newFilter: LenderFiltersState = {
              filterMetaState: {
                type: 'lender',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: {},
            };

            return [...prev, newFilter];
          });
        } else if (value === 'delivery_queued') {
          // TODO: we have to close the combobox here
          setFilters((prev) => {
            const newFilter: DeliveryQueuedFiltersState = {
              filterMetaState: {
                type: 'delivery_queued',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: false,
              },
              selectedFilters: {
                queued: true,
              },
            };

            return [...prev, newFilter];
          });
        } else if (value === 'price_locked') {
          // TODO: we have to close the combobox here
          setFilters((prev) => {
            const newFilter: PriceLockedFiltersState = {
              filterMetaState: {
                type: 'price_locked',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: false,
              },
              selectedFilters: {
                locked: true,
              },
            };

            return [...prev, newFilter];
          });
        } else if (value === 'traded_in') {
          setFilters((prev) => {
            const newFilter: TradeInFiltersState = {
              filterMetaState: {
                type: 'traded_in',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: false,
              },
              selectedFilters: {
                tradedIn: true,
              },
            };

            return [...prev, newFilter];
          });
        } else if (value === 'delivery_date') {
          setComboBoxState('delivery_date');
          setFilters((prev) => {
            const newFilter: DeliveryDateFiltersState = {
              filterMetaState: {
                type: 'delivery_date',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: 'none',
            };

            return [...prev, newFilter];
          });
        } else if (value === 'pickup_date') {
          setComboBoxState('pickup_date');
          setFilters((prev) => {
            const newFilter: PickupDateFiltersState = {
              filterMetaState: {
                type: 'pickup_date',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: 'none',
            };

            return [...prev, newFilter];
          });
        } else if (value === 'last_updated_date') {
          setComboBoxState('last_updated_date');
          setFilters((prev) => {
            const newFilter: LastUpdatedDateFiltersState = {
              filterMetaState: {
                type: 'last_updated_date',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: 'none',
            };

            return [...prev, newFilter];
          });
        } else if (value === 'created_date') {
          setComboBoxState('created_date');
          setFilters((prev) => {
            const newFilter: CreatedDateFiltersState = {
              filterMetaState: {
                type: 'created_date',
                position: 0,
                isNegated: false,
                isCurrentlyBeingBuilt: true,
              },
              selectedFilters: 'none',
            };

            return [...prev, newFilter];
          });
        }
      }

      if (
        DELIVERY_DATE_FILTER_OPTIONS.some((option) => option.value === value) ||
        LAST_UPDATED_DATE_FILTER_OPTIONS.some(
          (option) => option.value === value,
        ) ||
        CREATED_DATE_FILTER_OPTIONS.some((option) => option.value === value)
      ) {
        setLastFilter<DeliveryDateFiltersState>(
          (prev: DeliveryDateFiltersState) => {
            return {
              ...prev,
              selectedFilters: value as DeliveryDateFilterOptions,
              filterMetaState: {
                ...prev.filterMetaState,
                isCurrentlyBeingBuilt: false,
              },
            };
          },
        );

        // UX NOTE: after selecting a delivery date, we want to reset the combobox state to the filter state
        setComboBoxState('filter');

        return;
      }

      if (comboBoxState != 'filter') {
        setLastFilter<StatusFiltersState>((prev: StatusFiltersState) => {
          const _value = value as keyof StatusFilters;
          return {
            ...prev,
            selectedFilters: {
              ...prev.selectedFilters,
              [_value]: !prev.selectedFilters[_value],
            },
          };
        });

        return;
      }
    },
    [comboBoxState, setLastFilter],
  );

  const currentFilterState = useMemo(() => {
    const lastFilter = last(filters);

    if (!lastFilter?.filterMetaState.isCurrentlyBeingBuilt) {
      return null;
    }

    return lastFilter;
  }, [filters]);

  const comboBoxOpen = useMemo(() => {
    return comboBoxState != 'filter';
  }, [comboBoxState]);

  const filterButtonRef = useRef<HTMLDivElement>(null);

  useHotkeys(HOTKEYS.FILTERS, (e) => {
    // UX NOTE: prevent the combobox from having the "f" key
    // in the input after just pressing the hotkey
    e.preventDefault();
    e.stopPropagation();

    filterButtonRef.current?.click();
  });

  useEffect(() => {
    // UX NOTE: when `shouldApplyFilteredDealsIds` flips to false we should reset the
    // match preferences as well, because this is what users would expect when they use
    // the filters again
    if (!shouldApplyFilteredDealsIds) {
      setMatchPreference('match-any-filter');
    }
  }, [shouldApplyFilteredDealsIds]);

  const toggleMatchPreference = useCallback(() => {
    setMatchPreference((prev) => {
      if (prev == 'match-all-filters') return 'match-any-filter';

      return 'match-all-filters';
    });
  }, [setMatchPreference]);

  const toggleMyDealsFilter = useCallback(() => {
    setFilters((prev) => {
      if (!myDealsFilter) {
        return [...prev];
      }

      if (prev.some((filter) => isEqual(filter, myDealsFilter))) {
        return [...prev.filter((filter) => !isEqual(filter, myDealsFilter))];
      }

      return [myDealsFilter, ...prev];
    });
  }, [setFilters, myDealsFilter]);

  useHotkeys(HOTKEYS.FILTERS_TOGGLE_MATCH_ALL_OR_ANY, (e) => {
    e.preventDefault();
    e.stopPropagation();

    toggleMatchPreference();
  });

  useHotkeys(HOTKEYS.FILTERS_TOGGLE_MY_DEALS_FILTERS, (e) => {
    e.preventDefault();
    e.stopPropagation();

    toggleMyDealsFilter();
  });

  return (
    <div className="flex flex-row p-2 items-center justify-between gap-2 w-full">
      <div className="flex flex-row items-center gap-2 flex-wrap  max-w-screen">
        {filters.map((filter, index) => {
          const isCurrentlyBeingBuilt =
            filter.filterMetaState.isCurrentlyBeingBuilt;

          if (isCurrentlyBeingBuilt) {
            return null;
          }

          const isMyDealsFilter = isEqual(filter, myDealsFilter);

          const content = (
            <AllFilterChip
              key={`filter-${index}`}
              type={filter.filterMetaState.type}
              filtersState={filter as any}
              setFiltersState={setFilterAtIndex(index) as any}
              deleteFilter={removeFilterAtIndex(index)}
              shouldDeleteWhenNoFiltersAreSelected={true}
            />
          );

          if (isMyDealsFilter) {
            return (
              <Tooltip key={`filter-${index}`}>
                <TooltipTrigger>
                  <div key={`filter-${index}`}>{content}</div>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-row items-center gap-1">
                    <div>Toogle my deals filter</div>
                    <KeyboardKeyIcon>
                      {HOTKEYS.FILTERS_TOGGLE_MY_DEALS_FILTERS}
                    </KeyboardKeyIcon>
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          }

          return content;
        })}

        <AllFiltersComboBoxPopover
          // options={comboBoxOptions}
          onChange={comboBoxOnChange}
          // align="start"
          // TODO: Add end adornment and empty message
          // endAdornment={undefined}
          // emptyMessage={undefined}
          open={comboBoxOpen}
          onOpenChange={(open) => {
            if (!open) {
              // UX NOTE: When the combobox is closed, we want to reset the state to the filter state
              setComboBoxState('filter');

              if (!currentFilterState) {
                return;
              }

              if (
                values(currentFilterState.selectedFilters).every(
                  (value: boolean) => value === false,
                )
              ) {
                // TODO: handle this for all filter types
                // UX NOTE: If the current filter state has no selected filters, we want to remove it
                setFilters((prev) => {
                  return prev.slice(0, -1);
                });

                return;
              }

              setLastFilter<FilterState>((prev: any) => {
                return {
                  ...prev,
                  filterMetaState: {
                    ...prev.filterMetaState,
                    isCurrentlyBeingBuilt: false,
                  },
                };
              });
            }
          }}
          comboBoxState={comboBoxState}
          currentFilterState={currentFilterState}
          stayOpenAfterSelection
        >
          <div
            className="flex flex-row h-full items-center gap-2 cursor-pointer"
            ref={filterButtonRef}
          >
            {currentFilterState && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <AllFilterChip
                  type={currentFilterState.filterMetaState.type}
                  filtersState={currentFilterState as any}
                  setFiltersState={setFilterAtIndex(filters.length - 1) as any}
                  deleteFilter={removeFilterAtIndex(filters.length - 1)}
                  shouldDeleteWhenNoFiltersAreSelected={false}
                />
              </div>
            )}
            <PDKFilterIcon className="size-4" />
            <span className="text-md">Filter</span>
          </div>
        </AllFiltersComboBoxPopover>
      </div>
      <div className="md:flex flex-row items-center gap-2 hidden">
        {filters.length >= 2 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                className="text-xs"
                onClick={toggleMatchPreference}
              >
                {capitalize(matchPreference.split('-').join(' '))}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="flex flex-row items-center flex-nowrap gap-1">
              toggle match{' '}
              <KeyboardKeyIcon>
                {HOTKEYS.FILTERS_TOGGLE_MATCH_ALL_OR_ANY}
              </KeyboardKeyIcon>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
});

export default DealFilters;
